/**
 * Constantes do contrato entre cliente e servidor.
 *
 * Tudo aqui sai de um número já decidido em `docs/API.md`, `docs/WEBSOCKET.md`,
 * `docs/SERVER.md` ou `docs/SECURITY.md`. O valor mora neste pacote para que o
 * cliente e a API leiam o mesmo limite, em vez de cada lado repetir o número e
 * um deles esquecer de mudar.
 */
/** Prefixo das rotas da API. Mudança incompatível abre `/v2` (SERVER 8). */
export const API_VERSION = "v1";
/** Caminho base das rotas versionadas. */
export const API_BASE_PATH = `/${API_VERSION}`;
// ---------------------------------------------------------------------------
// Contas e sessões (SECURITY 2)
// ---------------------------------------------------------------------------
/** Tamanho mínimo da senha. */
export const PASSWORD_MIN_LENGTH = 10;
/** Teto de tamanho da senha, para não pagar Argon2id sobre texto gigante. */
export const PASSWORD_MAX_LENGTH = 200;
/** Validade do access token JWT, em segundos. */
export const ACCESS_TOKEN_TTL_SECONDS = 900;
/** Validade do refresh token opaco, em dias. */
export const REFRESH_TTL_DAYS = 30;
// ---------------------------------------------------------------------------
// Paginação (API 1)
// ---------------------------------------------------------------------------
/** Tamanho de página usado quando o cliente não pede outro. */
export const DEFAULT_PAGE_LIMIT = 50;
/**
 * Teto de itens por página.
 *
 * Os documentos só mostram `limit=50`. O teto é escolha deste pacote para que
 * nenhuma listagem vire uma varredura da tabela inteira.
 */
export const MAX_PAGE_LIMIT = 100;
// ---------------------------------------------------------------------------
// Limites de taxa e de tamanho (API 1, SECURITY 4)
// ---------------------------------------------------------------------------
/** Requisições por minuto, por usuário ou IP. */
export const RATE_LIMIT_PER_MINUTE = {
    auth: 10,
    read: 300,
    write: 60,
    chat: 20,
};
/** Corpo máximo de uma requisição comum, em bytes. */
export const MAX_BODY_BYTES = 2 * 1024 * 1024;
/** Corpo máximo do mundo de uma carreira, em bytes. */
export const MAX_SAVE_BYTES = 50 * 1024 * 1024;
// ---------------------------------------------------------------------------
// WebSocket (WEBSOCKET 1, 3, 6, 7, 8)
// ---------------------------------------------------------------------------
/**
 * Validade do ticket de conexão, em segundos.
 *
 * Sessenta e não trinta. O ticket é de uso único e consumido de forma atômica,
 * então a janela não é a defesa principal: quem intercepta um ticket já usado
 * não tem nada. Trinta segundos, por outro lado, é apertado no celular que sai
 * do wi-fi para o 4G no meio da reconexão, e o custo disso é a pessoa cair da
 * liga ao vivo e ter de entrar de novo.
 */
export const WS_TICKET_TTL_SECONDS = 60;
/** Intervalo do ping de protocolo. */
export const WS_HEARTBEAT_MS = 25_000;
/** Espera pelo pong antes de fechar a conexão com 1001. */
export const WS_PONG_TIMEOUT_MS = 10_000;
/** Conexões simultâneas por usuário. */
export const WS_MAX_CONNECTIONS_PER_USER = 5;
/** Tamanho máximo de uma mensagem, em bytes. */
export const WS_MAX_MESSAGE_BYTES = 16 * 1024;
/** Mensagens guardadas por canal para replay na reconexão. */
export const WS_REPLAY_MAX_MESSAGES = 500;
/** Validade do histórico de replay de um canal, em horas. */
export const WS_REPLAY_TTL_HOURS = 24;
/** Janela do limite de mensagens por conexão. */
export const WS_RATE_LIMIT = { messages: 30, windowMs: 10_000 };
/** Buffer de envio que faz o servidor descartar presença e digitação. */
export const WS_BACKPRESSURE_DROP_BYTES = 1024 * 1024;
/** Buffer de envio que faz o servidor fechar a conexão com 1013. */
export const WS_BACKPRESSURE_CLOSE_BYTES = 4 * 1024 * 1024;
/** Validade da chave de presença no Redis, em segundos. */
export const PRESENCE_TTL_SECONDS = 60;
/** Atraso de agrupamento antes de publicar `league.presence`. */
export const PRESENCE_DEBOUNCE_MS = 2_000;
/** Intervalo entre revalidações de autorização de canal, em segundos. */
export const CHANNEL_REAUTH_SECONDS = 300;
// ---------------------------------------------------------------------------
// Chat (API 13, WEBSOCKET 5, SECURITY 4)
// ---------------------------------------------------------------------------
/** Tamanho máximo de uma mensagem de chat, em caracteres. */
export const CHAT_MAX_LENGTH = 500;
/** Intervalo mínimo entre dois avisos de digitação. */
export const CHAT_TYPING_MIN_INTERVAL_MS = 3_000;
// ---------------------------------------------------------------------------
// Ligas e mercado (API 8, SECURITY 5)
// ---------------------------------------------------------------------------
/** Menor número de treinadores humanos em uma liga. */
export const LEAGUE_MIN_HUMANS = 2;
/** Maior número de treinadores humanos em uma liga. */
export const LEAGUE_MAX_HUMANS = 20;
/** Proporção mínima do valor de mercado em uma proposta entre humanos. */
export const MARKET_MIN_OFFER_RATIO = 0.7;
/** Piso que o comissário pode configurar para a proporção acima. */
export const MARKET_MIN_OFFER_RATIO_FLOOR = 0.5;
/** Negócios por dupla de clubes por temporada. */
export const MARKET_MAX_TRADES_PER_PAIR = 2;
/** Rodadas de espera antes de revender um jogador recém-comprado. */
export const MARKET_RESELL_COOLDOWN_ROUNDS = 3;
// ---------------------------------------------------------------------------
// Texto livre
// ---------------------------------------------------------------------------
/**
 * Faixas de nomes e textos curtos.
 *
 * Os documentos mandam limitar e sanitizar sem fixar os números, então estes
 * valores são escolha deste pacote e valem para os dois lados do protocolo.
 */
export const DISPLAY_NAME_MIN_LENGTH = 3;
export const DISPLAY_NAME_MAX_LENGTH = 32;
export const LEAGUE_NAME_MIN_LENGTH = 3;
export const LEAGUE_NAME_MAX_LENGTH = 40;
//# sourceMappingURL=constants.js.map